/**
 * LoginPage - Full-page authentication gate
 *
 * Shows GlowGhost branding with Google OAuth and access request controls.
 * Includes "Request Access" form for new users.
 * Rendered when user is not authenticated or not approved.
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase/client';
import LoginLogoShader from './login/LoginLogoShader';
import LoginShaderBackground from './login/LoginShaderBackground';
import {
  GlowGhostIcon,
  NATURAL_LANGUAGE_TEXT_INPUT_PROPS,
  PrimaryActionButton,
  SecondaryActionButton,
} from './ui';
import './LoginPage.css';

function LoginPage({ mode = 'login' }) {
  const { signInWithGoogle, signOut } = useAuth();
  const [view, setView] = useState(mode === 'pending' ? 'pending' : 'login');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const activeView = view === 'request' && submitted ? 'submitted' : view;

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('access_requests')
        .insert({ email: email.trim().toLowerCase(), name: name.trim() || null });

      if (insertError) {
        if (insertError.code === '23505') {
          // Unique constraint — already requested
          setSubmitted(true);
        } else {
          throw insertError;
        }
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <LoginShaderBackground />
      <div className="login-page-auth-card">
        <div className="login-page-brand-zone">
          <div className="login-page-brand-mark" role="img" aria-label="Modul">
            <LoginLogoShader />
            <GlowGhostIcon className="login-page-brand-mark__icon" />
          </div>
        </div>

        <div className="login-page-auth-controls">
          <AuthControlView active={activeView === 'login'} name="login">
            <div className="login-page-auth-stack">
              <SecondaryActionButton
                className="login-page-auth-button brand-bloom-hover"
                icon={<GoogleIcon />}
                onClick={signInWithGoogle}
              >
                Continue with Google
              </SecondaryActionButton>

              <SecondaryActionButton
                className="login-page-auth-button brand-bloom-hover"
                onClick={() => setView('request')}
              >
                Request Access
              </SecondaryActionButton>
            </div>
          </AuthControlView>

          <AuthControlView active={activeView === 'request'} name="request">
            <form className="login-page-auth-stack" onSubmit={handleRequestAccess}>
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                {...NATURAL_LANGUAGE_TEXT_INPUT_PROPS}
                className="login-page-auth-input"
              />
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                {...NATURAL_LANGUAGE_TEXT_INPUT_PROPS}
                className="login-page-auth-input"
              />

              {error && (
                <p className="login-page-error-copy">
                  {error}
                </p>
              )}

              <PrimaryActionButton
                type="submit"
                disabled={submitting || !email.trim()}
                loading={submitting}
                loadingText="Submitting..."
                className="login-page-auth-button brand-bloom-hover"
              >
                Request Access
              </PrimaryActionButton>

              <SecondaryActionButton
                type="button"
                className="login-page-auth-button brand-bloom-hover"
                onClick={() => setView('login')}
              >
                Back to sign in
              </SecondaryActionButton>
            </form>
          </AuthControlView>

          <AuthControlView active={activeView === 'submitted'} name="submitted">
            <div className="login-page-auth-stack">
              <p className="login-page-status-copy login-page-status-copy--submitted">
                Request Submitted
              </p>

              <SecondaryActionButton
                className="login-page-auth-button brand-bloom-hover"
                onClick={() => setView('login')}
              >
                Back to sign in
              </SecondaryActionButton>
            </div>
          </AuthControlView>

          <AuthControlView active={activeView === 'pending'} name="pending">
            <div className="login-page-auth-stack">
              <p className="login-page-status-copy">
                Your account is pending approval. Check back soon!
              </p>

              <SecondaryActionButton
                className="login-page-auth-button brand-bloom-hover"
                onClick={signOut}
              >
                Sign out
              </SecondaryActionButton>
            </div>
          </AuthControlView>
        </div>
      </div>
    </div>
  );
}

function AuthControlView({ active, children, name }) {
  return (
    <div
      className="login-page-auth-view"
      data-active={active}
      data-auth-view={name}
      aria-hidden={!active}
      inert={active ? undefined : ''}
    >
      {children}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default LoginPage;
